import { useWatch } from 'react-hook-form'
import { isEmpty } from 'lodash'
import {
    AddOfferField,
    filterMaileTypesFromGetValues,
    getMinQuantity,
    getPrice,
    MAILERS_QUANTITY_METAKEY,
    noOfRecordsField,
    pagesAndPrintingField,
    purchaseField,
    returnMailAddOnField,
    undelivarblesField,
} from '../../helpers/slug'
import { _BATCH_PRICE_KEY } from '../../helpers/checkout'
import { formatNumberLocal, formatCurrency } from '../../helpers/utils'
import {
    getAddOnQuantity,
    getAddOnToAddToCart,
} from '../../state/AddOnProductStore'

function CartInfo(props) {
    const currentValues = props.watch ? props.watch() : props.getValues()
    return <CartInfoView {...props} currentValues={currentValues} />
}

export function CYOCartInfo({ control, ...props }) {
    const watchedValues = useWatch({ control })
    const currentValues = { ...watchedValues, ...props.getValues() }
    return <CartInfoView {...props} currentValues={currentValues} />
}

function CartInfoView(props) {
    const {
        productCsvs,
        validateProductCsv = true,
        quantity,
        minQuantity,
        total: totalProp,
        register,
        price: originalPrice,
        isCampaignProduct,
        getMultiPlier,
        currentValues,
        cyoPrice,
        isListSourceProduct = false,
        addOnProducts = [],
    } = props

    const multiplier = getMultiPlier ? getMultiPlier() : 1
    const product = props.variation?.item || props.variation
    const isCyoCampaign =
        Boolean(props?.variation?.item?.slug || props?.variation?.slug) &&
        String(props?.variation?.item?.slug || props?.variation?.slug).includes(
            'create-your-own-campaign',
        )
    const resolvedMinQuantity =
        Number(minQuantity) > 0 ? Number(minQuantity) : getMinQuantity(product)

    const price = cyoPrice || originalPrice

    const isRecordsSetManually = !isEmpty(
        currentValues?.[noOfRecordsField.name],
    )

    const productsCsvValidation =
        (validateProductCsv ? productCsvs.length > 0 : true) ||
        isRecordsSetManually

    const baseTotal =
        typeof totalProp !== 'undefined'
            ? parseFloat(totalProp)
            : quantity * price * multiplier
    const addOnSummaryItem = {
        ...currentValues,
        quantity,
        ...(isCyoCampaign && { [MAILERS_QUANTITY_METAKEY]: Number(quantity) }),
        product: {
            name: product?.name || '',
            ...(isCyoCampaign && { slug: product.slug }),
        },
    }
    const additionalProductsList = getAddOnToAddToCart(addOnSummaryItem, {
        addOnProducts,
    }).filter((addOn) => addOn.option_name !== AddOfferField.name)
    const getAddOnLineTotal = (addOn) => {
        const addOnQuantity = getAddOnQuantity(addOn)

        if (addOn.option_name === purchaseField.name) {
            return (
                Number(getPrice(addOn.product, addOnQuantity, 1)) *
                addOnQuantity
            )
        }

        return (
            parseFloat(
                addOn?.[_BATCH_PRICE_KEY] || addOn?.product?.price || 0,
            ) * addOnQuantity
        )
    }
    const addOnsTotal = additionalProductsList.reduce((sum, addOn) => {
        return sum + getAddOnLineTotal(addOn)
    }, 0)
    const total = (baseTotal + addOnsTotal).toFixed(2)

    const addOnLabelMap = {
        [purchaseField.name]: purchaseField.label,
        [undelivarblesField.name]: undelivarblesField.label,
        [returnMailAddOnField.name]: returnMailAddOnField.label,
    }
    const getAddOnLabel = (addOn) => {
        if (
            isCyoCampaign &&
            /^Mailer Type \d+ Pages & Printing$/.test(addOn.option_name)
        ) {
            return `${addOn.option_name}: ${addOn[addOn.option_name]}`
        }

        if (addOn.option_name === pagesAndPrintingField.name) {
            return `${pagesAndPrintingField.label}: ${
                addOn[pagesAndPrintingField.name] ||
                pagesAndPrintingField.defaultValue
            }`
        }

        return addOnLabelMap[addOn.option_name] || addOn.option_name
    }
    const campaignMailerTypes = isCyoCampaign
        ? Object.values(filterMaileTypesFromGetValues(currentValues))
        : []

    if (!productsCsvValidation) return null

    return (
        productsCsvValidation && (
            <div className="mt-[18px] rounded-xl border border-[#ECE6DC] bg-white px-[22px] py-5 shadow-[0_1px_2px_rgba(26,29,35,0.04),0_8px_24px_rgba(26,29,35,0.06)]">
                <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.05em] text-gray-500">
                    {isCyoCampaign ? 'Campaign summary' : 'Order summary'}
                </h3>
                {!isListSourceProduct && (
                    <div className="flex items-baseline justify-between gap-3 py-[7px] text-[14.5px]">
                        <span className="block min-w-0 text-gray-500">
                            <span className="truncate">
                                {isCyoCampaign
                                    ? `Recipients (${resolvedMinQuantity} Min)`
                                    : `Quantity (${resolvedMinQuantity} Min)`}
                            </span>
                            <span className="font-semibold text-[#ED5C2F]">
                                {' '}
                                *
                            </span>
                        </span>
                        <span className="block text-[14.5px] font-semibold text-[#1A1D23]">
                            {formatNumberLocal(quantity)}
                        </span>
                    </div>
                )}
                <input
                    type="hidden"
                    {...register('quantity')}
                    value={quantity}
                />
                <div className="text-sm">
                    {isCampaignProduct && (
                        <>
                            {isCyoCampaign && (
                                <div className="flex items-baseline justify-between gap-3 py-[7px] text-[14.5px]">
                                    <span className="block min-w-0 truncate text-gray-500">
                                        Touches
                                    </span>
                                    <span className="block text-[14.5px] font-semibold text-[#1A1D23]">
                                        {formatNumberLocal(multiplier)}
                                    </span>
                                </div>
                            )}
                            {isCyoCampaign &&
                                campaignMailerTypes.map((mailerType, index) => (
                                    <div
                                        key={`${mailerType}-${index}`}
                                        className="flex items-baseline justify-between gap-3 py-[7px] text-[12.5px] text-gray-500">
                                        <span className="block min-w-0 truncate">
                                            {`Touch ${
                                                index + 1
                                            } · ${mailerType}`}
                                        </span>
                                        <span className="block whitespace-nowrap font-semibold text-[#1A1D23]">
                                            {formatCurrency(price, 2)}/pc
                                        </span>
                                    </div>
                                ))}
                        </>
                    )}
                    <div className="flex items-baseline justify-between gap-3 py-[7px] text-[14.5px]">
                        <span className="block min-w-0 truncate text-gray-500">
                            {isCyoCampaign ? 'Per recipient' : 'Each'}
                        </span>
                        <span className="block text-[14.5px] font-semibold text-[#1A1D23]">
                            {formatCurrency(price, 2)}
                        </span>
                    </div>
                    {isCampaignProduct && (
                        <>
                            <div className="flex items-baseline justify-between gap-3 py-[7px] text-[14.5px]">
                                <span className="block min-w-0 truncate text-gray-500">
                                    {isCyoCampaign
                                        ? 'Total pieces'
                                        : 'Total Mailers'}
                                </span>
                                <span className="block text-[14.5px] font-semibold text-[#1A1D23]">
                                    {formatNumberLocal(quantity * multiplier)}
                                </span>
                            </div>
                        </>
                    )}
                    {additionalProductsList.map((addOn) => (
                        <div
                            key={`${addOn.option_name}-${addOn.product?.id}`}
                            className="flex items-baseline justify-between gap-3 py-[7px] text-[12.5px] text-gray-500">
                            <span className="block min-w-0 truncate">
                                {getAddOnLabel(addOn)}
                            </span>
                            <span className="block whitespace-nowrap font-semibold text-[#1A1D23]">
                                {formatCurrency(getAddOnLineTotal(addOn), 2)}
                            </span>
                        </div>
                    ))}
                    <div className="mt-2 border-t-[1.5px] border-[#ECE6DC] pt-[14px]">
                        <div className="flex items-baseline justify-between gap-3 text-[17px] font-extrabold">
                            <span className="block min-w-0 truncate text-[#1A1D23]">
                                Total
                            </span>
                            <span className="block text-[24px] font-extrabold text-transparent bg-[linear-gradient(90deg,#ED5C2F,#F28230)] bg-clip-text [-webkit-text-fill-color:transparent]">
                                {formatCurrency(total, 2)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        )
    )
}

export default CartInfo
